/* eslint-disable no-unused-vars */
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '../axios/index';

/**
 * Maps client form state to GetMinimumRequiredBudget request body.
 *
 * Case 1 – New campaign: pass bid/bidType/target_type/placementsCount/numberOfCountries (no campaignId).
 * Case 2 – Full campaign edit: pass campaignId + all form fields (full snapshot from form state).
 * Case 3 – Budget-only edit: pass campaignId string, or { campaignId } with no other fields.
 *
 * numberOfCountries: null = "All Countries"; number = specific country count.
 */
export const mapToMinimumRequiredBudgetBody = (input) => {
  if (typeof input === 'string') {
    return { campaignId: input };
  }

  const { campaignId, bid, bidType, target_type, placementsCount, numberOfCountries } = input;

  const isBudgetOnlyEdit =
    campaignId &&
    bid === undefined &&
    bidType === undefined &&
    target_type === undefined &&
    placementsCount === undefined &&
    numberOfCountries === undefined;

  if (isBudgetOnlyEdit) {
    return { campaignId };
  }

  return {
    ...(campaignId ? { campaignId } : {}),
    targetCpi: bid ?? 0,
    isCpiCampaign: bidType === 3,
    isAllPlacements: target_type === 1,
    activePlacementsCount: placementsCount ?? 0,
    numberOfCountries: numberOfCountries ?? null,
  };
};

export const campaignsManageAPI = createApi({
  reducerPath: 'CampaignsManageAPI',
  baseQuery: axiosBaseQuery({ baseUrl: '/api/CampaignsManage' }),
  tagTypes: [],
  endpoints: (build) => ({
    getCampaignsAdvancedSettings: build.query({
      query: () => ({
        url: `/GetCampaignsAdvancedSettings`,
      }),
      providesTags: ['CampaignsManage/GetCampaignsAdvancedSettings'],
    }),
    saveCampaignsAdvancedSettings: build.mutation({
      query: (data) => ({
        url: '/SaveCampaignsAdvancedSettings',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['CampaignsManage/GetCampaignsAdvancedSettings'],
    }),
    getGlobalCampaignAdvancedSettings: build.query({
      query: () => ({
        url: `/GetCampaignsAdvancedSettings`,
      }),
      transformResponse: (response) => {
        const res = {
          excluded_apps: response.excluded_apps.map((data) => ({ ...data, isGlobal: true })),
          excluded_publishers: response.excluded_publishers.map((data) => ({
            ...data,
            isGlobal: true,
          })),
          typeList: response.typeList
            .filter(({ selected }) => selected)
            .map((data) => ({ ...data, isGlobal: true })),
        };
        return res;
      },
    }),
    /**
     * UA Budget Validation - Get minimum required budget.
     *
     * Accepts:
     * - campaignId string, or { campaignId } only → budget-only edit (backend uses DB).
     * - { campaignId, bid, bidType, target_type, placementsCount, numberOfCountries }
     *   → full campaign edit (backend uses form snapshot).
     *
     * Response: { minimumBudget, applicableRule }
     * @see JIRA: UA Campaign Budget Validation
     */
    getMinimumRequiredBudget: build.mutation({
      query: (input) => ({
        url: `/GetMinimumRequiredBudget`,
        method: 'POST',
        data: mapToMinimumRequiredBudgetBody(input),
      }),
    }),
  }),
});
